// components/ChatSetupFlow.jsx
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import StepDestination            from './steps/StepDestination';
import StepBudget                 from './steps/StepBudget';
import StepHotel                  from './steps/StepHotel';
import StepInterests              from './steps/StepInterests';
import StepDates                  from './steps/StepDates';
import StepTransport              from './steps/StepTransport';
import StepCompanions             from './steps/StepCompanions';
import StepDiet                   from './steps/StepDiet';
import StepMood                   from './steps/StepMood';
import StepExperience             from './steps/StepExperience';
import StepAdditionalDescription  from './steps/StepAdditionalDescription';
import StepSummary                from './steps/StepSummary';

const steps = [
  StepDestination,
  StepBudget,
  StepHotel,
  StepInterests,
  StepDates,
  StepTransport,
  StepCompanions,
  StepDiet,
  StepMood,
  StepExperience,
  StepAdditionalDescription,
  StepSummary,
];

const slideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeInOut' } },
  exit: (direction) => ({ x: direction < 0 ? 300 : -300, opacity: 0, transition: { duration: 0.3, ease: 'easeInOut' } }),
};

// expiration time for stored data (milliseconds)
const EXPIRATION_MS = 1000 * 60 * 60 * 0.5; // 24 hours

const ChatSetupFlow = ({ onSubmit, initialStepIndex = 0 }) => {
  const { id: chatId } = useParams();
  const prefix  = chatId ? `chat_${chatId}_` : '';
  const stepKey = `${prefix}tripStep`;
  const dataKey = `${prefix}tripData`;
  const tsKey   = `${prefix}tripDataTimestamp`;

  const [stepIndex, setStepIndex] = useState(() => {
    const savedStep = localStorage.getItem(stepKey);
    const savedTime = localStorage.getItem(tsKey);
    if (savedStep && savedTime && Date.now() - Number(savedTime) < EXPIRATION_MS) {
      return Number(savedStep);
    }
    return initialStepIndex;
  });

  const [tripData, setTripData] = useState(() => {
    const savedData = localStorage.getItem(dataKey);
    const savedTime = localStorage.getItem(tsKey);
    if (savedData && savedTime && Date.now() - Number(savedTime) < EXPIRATION_MS) {
      try {
        return JSON.parse(savedData);
      } catch {}
    }
    return {
      from: '', destination: '', budget: '', hasHotelReservation: false,
      interests: [], dates: { start: '', end: '' }, transport: '',
      companions: null, diet: [], mood: [], experience: '', additionalDescription: ''
    };
  });

  const [direction, setDirection] = useState(1);

  const persistTimestamp = () => {
    localStorage.setItem(tsKey, Date.now().toString());
  };

  const goToStep = (newIndex) => {
    if (newIndex < 0 || newIndex >= steps.length) return;
    setDirection(newIndex > stepIndex ? 1 : -1);
    setStepIndex(newIndex);
    localStorage.setItem(stepKey, String(newIndex));
    persistTimestamp();
  };

  const updateData = (field, value) => {
    setTripData(prev => {
      const updated = { ...prev, [field]: value };
      localStorage.setItem(dataKey, JSON.stringify(updated));
      persistTimestamp();
      return updated;
    });
    goToStep(stepIndex + 1);
  };

  const skipStep = () => goToStep(stepIndex + 1);
  const goBack   = () => goToStep(stepIndex - 1);
  const handleFinalSubmit = () => {
    localStorage.removeItem(dataKey);
    localStorage.removeItem(stepKey);
    localStorage.removeItem(tsKey);
    onSubmit(tripData);
  };
  const CurrentStep = steps[stepIndex];

  return (
    <div className="relative w-full max-h-[75vh] overflow-y-auto">
      <AnimatePresence custom={direction} mode="wait">
        <motion.div
          key={stepIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="w-full p-4"
        >
          <CurrentStep
            data={tripData}
            updateData={updateData}
            skip={skipStep}
            goBack={stepIndex > 0 ? goBack : null}
            onEdit={stepIndex === steps.length - 1 ? () => goToStep(0) : undefined}
            onSubmit={stepIndex === steps.length - 1 ? handleFinalSubmit : undefined}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ChatSetupFlow;
